import axios from 'axios';
import retry from 'async-retry';

import pkg from '../../package.json';
import { CropConfig } from '../hooks/useJoinGame';
import { ById } from '../typings/types';
import { getDestinationTenantFromUrl } from '../utils/navigator';
import { CuripodElement } from '@/components/SlideViewer/slateUtil';

const getUnAuthHeader = (destinationTenantId?: string) => {
  const base: ById<string> = {
    'x-app-version': `v${pkg.version}`,
  };
  if (destinationTenantId) {
    base['x-api-tenant'] = destinationTenantId;
  }
  return base;
};

// Without a timeout, a connection a network drops silently leaves the student
// on the loader forever instead of reaching the retry button.
const ATTACHMENT_REQUEST_TIMEOUT_MS = 10_000;

/**
 * Which of the two passage requests failed, and how. Step one
 * (`signed_url`) asks api.curipod.com for a signed URL. Step two
 * (`content`) downloads the JSON from Google Cloud Storage. Axios's own
 * "Network Error" does not say which request failed or to which host.
 */
export class AttachmentDownloadError extends Error {
  readonly stage: 'signed_url' | 'content';
  readonly host: string;
  /** HTTP status, or 'no response' for network errors and timeouts. */
  readonly status: string;
  /** Axios error code, e.g. 'ECONNABORTED' for a timeout. */
  readonly code: string;
  readonly durationMs: number;

  constructor(
    stage: 'signed_url' | 'content',
    url: string,
    cause: unknown,
    startedAt: number,
  ) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = 'AttachmentDownloadError';
    this.stage = stage;
    this.durationMs = Date.now() - startedAt;
    try {
      this.host = new URL(url).host;
    } catch {
      this.host = 'invalid url';
    }
    if (axios.isAxiosError(cause)) {
      this.status = cause.response?.status?.toString() ?? 'no response';
      this.code = cause.code ?? 'none';
    } else {
      this.status = 'n/a';
      this.code = 'none';
    }
  }
}

export default class BackendService {
  static instance: BackendService;
  url = '';
  idToken: string | null = null;
  deferredRefreshRequests: { resolve: () => void; reject: () => void }[] = [];
  isRefreshingTokens = false;
  constructor() {
    if (BackendService.instance) {
      return BackendService.instance;
    }
    BackendService.instance = this;
    return this;
  }

  public init(url: string) {
    BackendService.instance.url = url;

    if (process.env.isLocal) {
      BackendService.instance.url = 'http://localhost:3010';
    }
  }
  public async getPublicDownloadUrl(
    filename: string,
    tenantId: string,
    folder?: string,
  ) {
    return await axios.post<{ url: string }>(
      `${this.url}/v0.5/files/public/downloadUrl`,
      { filename: filename, folder },
      {
        headers: {
          ...getUnAuthHeader(tenantId),
        },
      },
    );
  }
  gameFiles = {
    fetchAttachmentContent: async ({
      fileId,
      tenantId,
    }: {
      fileId: string;
      tenantId: string;
    }): Promise<CuripodElement[]> => {
      const url = `${this.url}/v0.5/game_images/public/filesV2/${fileId}`;
      const signedUrlStartedAt = Date.now();
      let signedUrl: string;
      try {
        const res = await axios.request<string>({
          method: 'get',
          url,
          headers: {
            ...getUnAuthHeader(tenantId),
            'Cache-Control': 'no-cache', // Skip cache, as the backend will try to set a cache header.
          },
          timeout: ATTACHMENT_REQUEST_TIMEOUT_MS,
        });
        signedUrl = res.data;
      } catch (err) {
        throw new AttachmentDownloadError(
          'signed_url',
          url,
          err,
          signedUrlStartedAt,
        );
      }

      // Step two goes to a different host (storage.googleapis.com) than the
      // API. School content filters can block or intercept it while
      // api.curipod.com still works, so the failure is tagged with its stage.
      const contentStartedAt = Date.now();
      try {
        // We fetch the content of the file which is a json type. Responsetype json automatically parses the json
        const rawFileContent = await axios.request<{
          version: number;
          type: 'passage';
          content: CuripodElement[];
        }>({
          method: 'get',
          url: signedUrl,
          responseType: 'json',
          timeout: ATTACHMENT_REQUEST_TIMEOUT_MS,
        });
        if (!Array.isArray(rawFileContent.data?.content)) {
          // A proxy or captive portal answering with its own HTML page still
          // gives a 200, so check the body looks like a passage.
          throw new Error('Passage body is not valid passage JSON');
        }
        return rawFileContent.data.content;
      } catch (err) {
        throw new AttachmentDownloadError(
          'content',
          signedUrl,
          err,
          contentStartedAt,
        );
      }
    },
    fetchUauthenticatedCroppedImage: async ({
      fileId,
      isFromDiscover,
      cropConfig,
      tenantId,
    }: {
      fileId: string;
      cropConfig?: CropConfig | null;
      isFromDiscover: boolean;
      tenantId?: string;
    }) =>
      await retry(async (retrier, attemptNumber) => {
        const query = cropConfig
          ? `?h=${cropConfig.height}&w=${cropConfig.width}&x=${cropConfig.x}&y=${cropConfig.y}`
          : undefined;
        if (isFromDiscover) {
          const url = `${this.url}/v0.5/discover/public/filesV2/${fileId}${
            query ? query : ''
          }`;

          const res = await axios.request<string>({
            method: 'get',
            url,
            headers: {
              ...(attemptNumber > 1 && { 'Cache-Control': 'no-cache' }),
            },
          });

          const signedUrl = res.data;

          //we want to prefetch the image so also get the image blob from cdn
          const cdnImageResponse = await axios.request<Blob>({
            method: 'get',
            url: signedUrl,
            responseType: 'blob',
          });
          const objectUrl = URL.createObjectURL(cdnImageResponse.data);
          return objectUrl;
        }
        // This image is not from discover - we use game_images endpoints

        const url = `${this.url}/v0.5/game_images/public/filesV2/${fileId}${
          query ? query : ''
        }`;

        const res = await axios.request<string>({
          method: 'get',
          url,
          headers: {
            ...getUnAuthHeader(tenantId),
            ...(attemptNumber > 1 && { 'Cache-Control': 'no-cache' }),
          },
        });

        const signedUrl = res.data;

        //we want to prefetch the image so also get the image blob from cdn
        const cdnImageResponse = await axios.request<Blob>({
          method: 'get',
          url: signedUrl,
          responseType: 'blob',
        });
        const objectUrl = URL.createObjectURL(cdnImageResponse.data);
        return objectUrl;
      }),
    fetchSvgImage: async ({
      fileId,
      cropConfig,
      tenantId,
    }: {
      fileId: string;
      cropConfig?: CropConfig | null;
      tenantId: string;
    }) =>
      await retry(async (retrier, attemptNumber) => {
        const query = cropConfig
          ? `?h=${cropConfig.height}&w=${cropConfig.width}&x=${cropConfig.x}&y=${cropConfig.y}`
          : undefined;

        const url = `${this.url}/v0.5/game_images/public/filesV2/${fileId}${
          query ? query : ''
        }`;

        const res = await axios.request<string>({
          method: 'get',
          url,
          headers: {
            ...getUnAuthHeader(tenantId),
            ...(attemptNumber > 1 && { 'Cache-Control': 'no-cache' }),
          },
        });

        const signedUrl = res.data;

        //we want to prefetch the image so also get the image blob from cdn
        const cdnImageResponse = await axios.request({
          method: 'get',
          url: signedUrl,
        });

        return cdnImageResponse.data;
      }),
  };
}
